/**
 * Вид карантина для экрана: строки «под наблюдением». Чистые функции без UI.
 * Массивов как оси здесь нет (решение №85).
 *
 * Счёты — на сервере (S4, журнал №960): клиент НЕ считает красных, строки
 * «после срока», дни просрочки, «устранено за 30 дней» и строки хозяйств. Он
 * показывает то, что назвал сервер: `requirement_overdue` и `overdue_days` —
 * поля строки, «Хозяйства с очагами» — `summary.farm_hotspots`, числа KPI —
 * метрики слоя. Здесь остаётся только порядок строк и шкала ползунка.
 *
 * Правило канона: отсутствие не ноль. Срок предписания, хозяйство и вид, которых
 * источник не назвал, остаются `null` и печатаются прочерком.
 */
import type { QuarantineStatus } from '../data/types';
import { defineMessages } from '../../i18n/define';
import { lazyWords } from '../lib/lazyWords';

const m = defineMessages({
  pests: { id: 'quarantine.view.direction.pests', defaultMessage: 'вредитель', description: 'Направление находки (когда вид не назван): вредитель; строчными, в ячейке таблицы' },
  weeds: { id: 'quarantine.view.direction.weeds', defaultMessage: 'сорняк', description: 'Направление находки (когда вид не назван): сорняк; строчными, в ячейке таблицы' },
  diseases: { id: 'quarantine.view.direction.diseases', defaultMessage: 'болезнь', description: 'Направление находки (когда вид не назван): болезнь растений; строчными, в ячейке таблицы' },
});

export const DIRECTION_WORD: Record<string, string> = lazyWords({
  pests: m.pests,
  weeds: m.weeds,
  diseases: m.diseases,
});

const DAY = 86_400_000;
const ms = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);

export interface WatchRow {
  status: QuarantineStatus;
  /**
   * Хозяйства контура — ОДНО правило с кабинетом и ИИ (журнал №652): все
   * землепользования контура на дату, их называет сервер (`farm_ids`).
   * Пусто — не сопоставлено.
   */
  farmIds: string[];
  species: string | null;
  deadline: string | null;
  /** Срок прошёл, а устранения замером нет — флаг сервера; `null` — срока нет. */
  overdue: boolean | null;
  /** Дней после срока — сервер; `null` — просрочки нет. */
  overdueDays: number | null;
}

export function watchRows(
  statuses: QuarantineStatus[],
  farmOf: (contourId: string) => string | null = () => null,
): WatchRow[] {
  return statuses
    .filter(s => s.status !== 'green')
    .map(status => {
      const fallback = farmOf(status.contour_id);
      return {
        status,
        farmIds: status.farm_ids ?? (fallback ? [fallback] : []),
        species: status.problem_name ?? null,
        deadline: status.requirement_deadline ?? null,
        overdue: status.requirement_overdue ?? null,
        overdueDays: status.requirement_overdue ? (status.overdue_days ?? null) : null,
      };
    })
    // Сначала просроченные (дольше — выше), затем красные, затем свежие.
    .sort((a, b) => Number(b.overdue === true) - Number(a.overdue === true)
      || (b.overdueDays ?? 0) - (a.overdueDays ?? 0)
      || Number(b.status.status === 'red') - Number(a.status.status === 'red')
      || b.status.since.localeCompare(a.status.since));
}

/** Первая дата ленты статусов — начало шкалы ползунка; `null` — лента пуста. */
export function firstStatusDate(statuses: QuarantineStatus[]): string | null {
  let first: string | null = null;
  for (const s of statuses) if (s.since && (first === null || s.since < first)) first = s.since.slice(0, 10);
  return first;
}

/**
 * Начало недельной шкалы, выровненное от конца: точки шкалы ложатся на
 * `to − 7·k`, и дата ровно за N недель до конца выбирается точно.
 */
export function weekAlignedFrom(first: string, to: string): string {
  const span = Math.max(0, Math.round((ms(to) - ms(first)) / DAY));
  const weeks = Math.ceil(span / 7);
  return new Date(ms(to) - weeks * 7 * DAY).toISOString().slice(0, 10);
}
